import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Form, Row, Alert } from 'react-bootstrap';
import jwt_decode from 'jwt-decode';
import MisIntegrationApi from '../../../api/MisIntegrationApi';
import LookupLayout from '../../layout/LookupLayout';

function toDatetimeLocalValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const MisSettingsManager = () => {
  const isAdmin = useMemo(() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return false;
      const decoded = jwt_decode(token);
      return String(decoded?.role_name || '').toUpperCase() === 'ADMIN';
    } catch {
      return false;
    }
  }, []);

  const [defaultCrmUserId, setDefaultCrmUserId] = useState('');
  const [defaultCrmUserLabel, setDefaultCrmUserLabel] = useState('');
  const [defaultModifiedById, setDefaultModifiedById] = useState('');
  const [defaultModifiedByLabel, setDefaultModifiedByLabel] = useState('');
  const [clientApiKey, setClientApiKey] = useState('');
  const [clientId, setClientId] = useState('');
  const [apiBaseUrl, setApiBaseUrl] = useState('');
  const [apiBaseUrlResolved, setApiBaseUrlResolved] = useState('');
  const [syncNotificationsEnabled, setSyncNotificationsEnabled] = useState(true);
  const [notificationIntervalMinutes, setNotificationIntervalMinutes] = useState('120');
  const [alwaysNotifyOnManualRun, setAlwaysNotifyOnManualRun] = useState(true);
  const [autoCreateLead, setAutoCreateLead] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [clientApiKeyConfigured, setClientApiKeyConfigured] = useState(false);
  const [lastSyncTimeInput, setLastSyncTimeInput] = useState('');
  const [syncScheduler, setSyncScheduler] = useState(null);
  const [lastScriptRun, setLastScriptRun] = useState(null);
  const [webhookPath, setWebhookPath] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ variant: '', text: '' });
  const [webhookCopyHint, setWebhookCopyHint] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setMessage({ variant: '', text: '' });
    try {
      const res = await MisIntegrationApi.fetchSettings();
      const data = res?.data?.data;
      if (res?.data?.success && data) {
        setDefaultCrmUserId(data.default_user_id || '');
        setDefaultCrmUserLabel(data.default_user_label || '');
        setDefaultModifiedById(data.default_modified_by_id || '');
        setDefaultModifiedByLabel(data.default_modified_by_label || '');
        setApiBaseUrl(data.api_base_url != null ? String(data.api_base_url) : '');
        setApiBaseUrlResolved(
          data.api_base_url_resolved != null
            ? String(data.api_base_url_resolved)
            : 'https://apps.apimis.co.in'
        );
        setSyncNotificationsEnabled(data.sync_notifications_enabled !== false);
        setNotificationIntervalMinutes(
          data.notification_interval_minutes != null
            ? String(data.notification_interval_minutes)
            : data.notification_interval_hours != null
              ? String(Number(data.notification_interval_hours) * 60)
              : '120'
        );
        setAlwaysNotifyOnManualRun(data.always_notify_on_manual_run !== false);
        setAutoCreateLead(data.auto_create_lead === true);
        setIsActive(data.is_active === true);
        setClientApiKeyConfigured(data.client_api_key_configured === true);
        setLastSyncTimeInput(toDatetimeLocalValue(data.last_sync_time));
        setSyncScheduler(data.sync_scheduler || null);
        setLastScriptRun(data.last_script_run || null);
        setWebhookPath(data.webhook_path || '');
        setClientId(
          data.client_id != null && data.client_id !== ''
            ? String(data.client_id)
            : ''
        );
        setClientApiKey('');
      } else {
        setMessage({
          variant: 'danger',
          text: res?.data?.message || 'Failed to load MIS settings.',
        });
      }
    } catch (e) {
      setMessage({
        variant: 'danger',
        text:
          e?.response?.data?.message ||
          e?.message ||
          'Failed to load MIS settings.',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;

    setSaving(true);
    setMessage({ variant: '', text: '' });
    if (!defaultModifiedById || defaultModifiedById.trim() === '') {
      setMessage({ variant: 'danger', text: 'Default Last Modified By is required.' });
      setSaving(false);
      return;
    }
    try {
      const body = {
        default_user_id: defaultCrmUserId.trim() === '' ? null : defaultCrmUserId.trim(),
        default_modified_by_id: defaultModifiedById.trim() === '' ? null : defaultModifiedById.trim(),
        api_base_url: apiBaseUrl.trim() === '' ? null : apiBaseUrl.trim(),
        sync_notifications_enabled: syncNotificationsEnabled,
        notification_interval_minutes: Number(notificationIntervalMinutes),
        always_notify_on_manual_run: alwaysNotifyOnManualRun,
        auto_create_lead: autoCreateLead,
        is_active: isActive,
        client_id:
          clientId.trim() === '' ? null : Number(clientId.trim()),
        last_sync_time:
          lastSyncTimeInput.trim() === ''
            ? null
            : new Date(lastSyncTimeInput).toISOString(),
      };
      if (clientApiKey.trim() !== '') {
        body.client_api_key = clientApiKey.trim();
      }
      const res = await MisIntegrationApi.saveSettings(body);
      if (res?.data?.success) {
        setMessage({ variant: 'success', text: 'MIS settings saved.' });
        setClientApiKey('');
        const data = res?.data?.data;
        if (data) {
          setDefaultCrmUserId(data.default_user_id || '');
          setDefaultCrmUserLabel(data.default_user_label || '');
          setDefaultModifiedById(data.default_modified_by_id || '');
          setDefaultModifiedByLabel(data.default_modified_by_label || '');
          setApiBaseUrl(data.api_base_url != null ? String(data.api_base_url) : '');
          setApiBaseUrlResolved(
            data.api_base_url_resolved != null
              ? String(data.api_base_url_resolved)
              : 'https://apps.apimis.co.in'
          );
          setSyncNotificationsEnabled(data.sync_notifications_enabled !== false);
          setNotificationIntervalMinutes(
            data.notification_interval_minutes != null
              ? String(data.notification_interval_minutes)
              : data.notification_interval_hours != null
                ? String(Number(data.notification_interval_hours) * 60)
                : '120'
          );
          setAlwaysNotifyOnManualRun(data.always_notify_on_manual_run !== false);
          setAutoCreateLead(data.auto_create_lead === true);
          setClientId(
            data.client_id != null && data.client_id !== ''
              ? String(data.client_id)
              : ''
          );
          setIsActive(data.is_active === true);
          setClientApiKeyConfigured(data.client_api_key_configured === true);
          setLastSyncTimeInput(toDatetimeLocalValue(data.last_sync_time));
          setWebhookPath(data.webhook_path || '');
        }
        try {
          const refreshRes = await MisIntegrationApi.fetchSettings();
          const refreshed = refreshRes?.data?.data;
          if (refreshed?.sync_scheduler) {
            setSyncScheduler(refreshed.sync_scheduler);
          }
        } catch {
          // ignore refresh errors; save already succeeded
        }
      } else {
        setMessage({
          variant: 'danger',
          text: res?.data?.message || 'Save failed.',
        });
      }
    } catch (e) {
      setMessage({
        variant: 'danger',
        text:
          e?.response?.data?.message ||
          e?.message ||
          'Save failed.',
      });
    } finally {
      setSaving(false);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleString();
    } catch {
      return String(value);
    }
  };

  const schedulerSummary = useMemo(() => {
    if (!syncScheduler?.configured) {
      return 'No scheduled sync job found. Create one in Setup → Scheduled Jobs (script: sync_miscall_log_controller).';
    }
    const parts = [];
    parts.push(syncScheduler.is_active ? 'Active' : 'Inactive');
    if (syncScheduler.status) {
      parts.push(`status: ${syncScheduler.status}`);
    }
    if (syncScheduler.repeat_type) {
      const interval = syncScheduler.repeat_interval || 1;
      const unit = String(syncScheduler.repeat_type).toLowerCase();
      if (unit === 'minutes' || unit === 'minutely') {
        parts.push(`every ${interval} minute${interval === 1 ? '' : 's'}`);
      } else {
        parts.push(`every ${interval} ${unit}`);
      }
    }
    return parts.join(' · ');
  }, [syncScheduler]);

  if (loading) {
    return <div className="p-3">Loading…</div>;
  }

  return (
    <div className="p-3" style={{ maxWidth: 720 }}>
      <h4 className="mb-3">MIS Integration</h4>
      <p className="text-muted small">
        Configure the organization MIS client API key (stored encrypted).
        Enable the MIS integration package on your subscription in the admin panel first.
      </p>

      {!isAdmin && (
        <Alert variant="info">
          Only administrators can change MIS credentials.
        </Alert>
      )}

      {message.text && (
        <Alert variant={message.variant || 'info'} className="mt-2">
          {message.text}
        </Alert>
      )}

      <Form onSubmit={handleSave}>
        <Card className="mt-3">
          <Card.Body>
            <Form.Group className="mb-3" controlId="mis-active">
              <Form.Check
                type="switch"
                id="mis-is-active"
                label="MIS Active"
                checked={isActive}
                onChange={(ev) => setIsActive(ev.target.checked)}
                disabled={!isAdmin}
              />
              <Form.Text className="text-muted">
                When off, incoming call records via webhook are rejected, and manual sync is disabled.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" controlId="mis-auto-create-lead">
              <Form.Check
                type="switch"
                id="mis-auto-create-lead-switch"
                label="Auto Create Lead"
                checked={autoCreateLead}
                onChange={(ev) => setAutoCreateLead(ev.target.checked)}
                disabled={!isAdmin}
              />
              <Form.Text className="text-muted">
                When enabled, incoming MIS call records will automatically create a new Lead in CRM if the customer mobile number does not exist. If disabled, leads will not be created automatically.
              </Form.Text>
            </Form.Group>

            <Row className="g-3">
              <Col md={12}>
                <Form.Group controlId="mis-default-crm-user">
                  <Form.Label>Default Record Owner</Form.Label>
                  <LookupLayout
                    objectName="user"
                    searchFields="firstname,lastname, username"
                    displayFields="firstname,lastname, username"
                    value={defaultCrmUserId || null}
                    defaultLabel={defaultCrmUserLabel || ''}
                    onChange={(id, label) => {
                      setDefaultCrmUserId(id || '');
                      setDefaultCrmUserLabel(label || '');
                    }}
                    placeholder="Search users…"
                    disabled={!isAdmin}
                  />
                  <Form.Text className="text-muted">
                    Incoming webhooks and sync calls set mis_log owner to this user.
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group controlId="mis-default-modified-user">
                  <Form.Label>Default Created By & Last Modified By <span className="text-danger">*</span></Form.Label>
                  <LookupLayout
                    objectName="user"
                    searchFields="firstname,lastname, username"
                    displayFields="firstname,lastname, username"
                    value={defaultModifiedById || null}
                    defaultLabel={defaultModifiedByLabel || ''}
                    onChange={(id, label) => {
                      setDefaultModifiedById(id || '');
                      setDefaultModifiedByLabel(label || '');
                    }}
                    placeholder="Search users…"
                    disabled={!isAdmin}
                  />
                  <Form.Text className="text-muted">
                    Required. Incoming webhooks and sync calls set mis_log created by and last modified by to this user.
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group controlId="mis-api-base-url">
                  <Form.Label>API Base URL</Form.Label>
                  <Form.Control
                    value={apiBaseUrl}
                    onChange={(ev) => setApiBaseUrl(ev.target.value)}
                    placeholder="https://apps.apimis.co.in"
                    disabled={!isAdmin}
                    autoComplete="off"
                  />
                  <Form.Text className="text-muted">
                    MIS API host used for call sync (
                    <code className="small">{'{API Base URL}'}/api/getAllCallRecords</code>
                    ). Leave blank to use{' '}
                    {apiBaseUrlResolved ? (
                      <code className="small">{apiBaseUrlResolved}</code>
                    ) : (
                      <code className="small">https://apps.apimis.co.in</code>
                    )}
                    .
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group controlId="mis-sync-notifications-enabled">
                  <Form.Check
                    type="switch"
                    id="mis-sync-notifications-enabled"
                    label="Sync notifications"
                    checked={syncNotificationsEnabled}
                    onChange={(ev) => setSyncNotificationsEnabled(ev.target.checked)}
                    disabled={!isAdmin}
                  />
                  <Form.Text className="text-muted">
                    Turn off to run MIS sync (scheduler and manual) with no in-app notifications.
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group controlId="mis-notification-interval-minutes">
                  <Form.Label>Notification throttle (minutes)</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    max={10080}
                    step={1}
                    value={notificationIntervalMinutes}
                    onChange={(ev) => setNotificationIntervalMinutes(ev.target.value)}
                    disabled={!isAdmin || !syncNotificationsEnabled}
                  />
                  <Form.Text className="text-muted">
                    Does not change sync frequency. Limits in-app &ldquo;Sync Started / Complete&rdquo;
                    to at most once per this many minutes when the scheduler runs. Default: 120.
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group controlId="mis-always-notify-manual" className="mt-md-4 pt-md-2">
                  <Form.Check
                    type="switch"
                    id="mis-always-notify-manual-run"
                    label="Notify on manual Sync Call"
                    checked={alwaysNotifyOnManualRun}
                    onChange={(ev) => setAlwaysNotifyOnManualRun(ev.target.checked)}
                    disabled={!isAdmin || !syncNotificationsEnabled}
                  />
                  <Form.Text className="text-muted">
                    When on, list Sync Call and API runs send notifications (not throttled by interval).
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group controlId="mis-client-id">
                  <Form.Label>Client ID (sync filter)</Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    step={1}
                    value={clientId}
                    onChange={(ev) => setClientId(ev.target.value)}
                    placeholder="Leave blank to sync all clients"
                    disabled={!isAdmin}
                  />
                  <Form.Text className="text-muted">
                    When set, only call records whose MIS{' '}
                    <code className="small">clientId_fk</code> matches this value are saved to{' '}
                    <code className="small">mis_log.clientid_fk</code>. Leave blank to import every
                    client returned by the API.
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group controlId="mis-client-api-key">
                  <Form.Label>Client API Key</Form.Label>
                  <Form.Control
                    type="password"
                    value={clientApiKey}
                    onChange={(ev) => setClientApiKey(ev.target.value)}
                    placeholder={
                      clientApiKeyConfigured
                        ? 'Leave blank to keep existing key'
                        : 'Required when MIS integration is active'
                    }
                    disabled={!isAdmin}
                    autoComplete="new-password"
                  />
                  {clientApiKeyConfigured && (
                    <Form.Text className="text-muted">
                      A client API key is already stored. Enter a new value only to replace it.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group controlId="mis-last-sync-time">
                  <Form.Label>Last successful sync</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={lastSyncTimeInput}
                    onChange={(ev) => setLastSyncTimeInput(ev.target.value)}
                    disabled={!isAdmin}
                  />
                  <Form.Text className="text-muted">
                    Used as the MIS API <code className="small">fromDate</code> on the next sync.
                    Leave blank and save to re-sync all call records from the beginning (from
                    01-01-2010). After a successful sync, this is updated automatically to now.
                    {lastSyncTimeInput.trim() === '' && (
                      <>
                        {' '}
                        <span className="text-warning">
                          Currently blank — next sync will fetch full history.
                        </span>
                      </>
                    )}
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group controlId="mis-scheduler-status">
                  <Form.Label>Scheduled sync job</Form.Label>
                  <Form.Control
                    type="text"
                    value={schedulerSummary}
                    disabled
                    readOnly
                  />
                  {syncScheduler?.configured && (
                    <Form.Text className="text-muted d-block">
                      Sync timing is managed in Setup → Scheduled Jobs (this page does not change it).
                      <br />
                      Last scheduler run: {formatDateTime(syncScheduler.last_run)}
                      {' · '}
                      Next run: {formatDateTime(syncScheduler.next_run)}
                      {syncScheduler.failure_cleared_as_stale && (
                        <br />
                      )}
                      {syncScheduler.failure_cleared_as_stale && (
                        <span className="text-success">
                          A previous error about missing database columns was cleared
                          (columns are now present). Wait for the next scheduled run or use
                          Setup → Scheduled Jobs → Run now.
                        </span>
                      )}
                      {syncScheduler.failure_reason ? (
                        <>
                          <br />
                          <span className="text-danger">
                            Last scheduler error (saved on job):{' '}
                            {String(syncScheduler.failure_reason)}
                          </span>
                          {(/notification_interval_minutes|notification_interval_hours/i.test(
                            String(syncScheduler.failure_reason)
                          )) &&
                            !syncScheduler.notification_columns_ready && (
                              <>
                                <br />
                                <span className="text-warning">
                                  Run migrations/mis_setting_notification_interval_minutes.sql on
                                  your tenant schema, then restart the backend.
                                </span>
                              </>
                            )}
                        </>
                      ) : null}
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>

              {lastScriptRun && (
                <Col md={12}>
                  <Form.Group controlId="mis-last-script-run">
                    <Form.Label>Last script execution</Form.Label>
                    <Form.Control
                      type="text"
                      value={
                        lastScriptRun.success
                          ? `OK — ${formatDateTime(lastScriptRun.createddate)}${lastScriptRun.message ? ` — ${lastScriptRun.message}` : ''}`
                          : `Failed — ${formatDateTime(lastScriptRun.createddate)} — ${lastScriptRun.error || 'Unknown error'}`
                      }
                      disabled
                      readOnly
                      className={lastScriptRun.success ? '' : 'border-danger'}
                    />
                  </Form.Group>
                </Col>
              )}
            </Row>
          </Card.Body>
        </Card>

        <Card className="mt-3">
          <Card.Body>
            <Card.Title as="h6">Webhook URL</Card.Title>
            <p className="text-muted small mb-3">
              Paste this URL into the MIS portal settings. Incoming call status callbacks from the MIS server will post directly to this webhook to log calls in CRM.
            </p>
            <Form.Group className="mb-0">
              <Form.Label>Webhook URL</Form.Label>
              <div className="d-flex gap-2 flex-wrap">
                <Form.Control
                  readOnly
                  value={webhookPath}
                  placeholder={webhookPath ? undefined : 'Not available (sign in with a company context).'}
                />
                <Button
                  type="button"
                  variant="outline-secondary"
                  disabled={!webhookPath}
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(webhookPath);
                      setWebhookCopyHint('webhook');
                      window.setTimeout(() => setWebhookCopyHint(''), 2000);
                    } catch {
                      setMessage({ variant: 'warning', text: 'Could not copy Webhook URL.' });
                    }
                  }}
                >
                  Copy
                </Button>
              </div>
              {webhookCopyHint === 'webhook' && (
                <Form.Text className="text-success">Copied.</Form.Text>
              )}
            </Form.Group>
          </Card.Body>
        </Card>

        {isAdmin && (
          <div className="mt-3 pt-3 border-top d-flex gap-2 flex-wrap">
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button type="button" variant="outline-secondary" onClick={load} disabled={saving}>
              Reload
            </Button>
          </div>
        )}
      </Form>
    </div>
  );
};

export default MisSettingsManager;
